import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { useMutation } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import {
  refundEnrollment,
  RefundEnrollmentInput,
  RefundEnrollmentResponse
} from '../../../../api/enrollments/refund-enrollment'
import {
  PaymentMeansEnum,
  paymentMeansEnumMapping
} from '../../../../lib/types/enums/payment-record/payment-means.enum'
import { modalButtonConfig } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import {
  getSelectedEnumOption,
  showAxiosError
} from '../../../../lib/utils/functions/misc.functions'
import { createEnumSchema } from '../../../../lib/utils/validations/enum-validation'
import { createNumberSchema } from '../../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../../elements/validation-buttons'

const formSchema: ZodType<RefundEnrollmentInput> = z.object({
  amount: createNumberSchema('Montant', { isPositive: true, isInteger: false }),
  reason: createStringSchema('Motif', { nullish: true }),
  means: createEnumSchema({ enumType: PaymentMeansEnum, fieldName: 'moyen de remboursement' })
})

// the gateway rejected the refund, or only part of it: the failed lines show in the payment table
export const REFUND_FAILED_MESSAGE =
  "Le remboursement n'a pas pu être effectué en totalité. Veuillez vérifier les détails des paiements."

type Props = {
  enrollmentId: number
  // the overpayment to refund, when the form is opened after a price drop
  defaultAmount?: number
  confirmText?: string
  cancelText?: string
  className?: string
  onSuccess: (data: RefundEnrollmentResponse) => void
  onCancel: () => void
}

const RefundEnrollmentForm: React.FC<Props> = ({
  enrollmentId,
  defaultAmount,
  confirmText = 'Valider',
  cancelText = 'Fermer',
  className,
  onSuccess,
  onCancel
}) => {
  const form = useForm<RefundEnrollmentInput>({
    resolver: zodResolver(formSchema),
    defaultValues: { amount: defaultAmount, means: PaymentMeansEnum.CREDIT_CARD },
    mode: FORM_MODE
  })

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: RefundEnrollmentInput) => refundEnrollment({ id: enrollmentId, ...data }),
    onError: showAxiosError,
    onSuccess
  })

  const buttonsConfig = modalButtonConfig<RefundEnrollmentInput>({
    confirmText,
    cancelText,
    onConfirm: (data) => mutateAsync(data),
    onCancel,
    isLoading: isPending
  })

  return (
    <Form {...form}>
      <form
        className={cn('flex flex-col', className)}
        onSubmit={form.handleSubmit((data) => mutateAsync(data))}
      >
        <div className="flex flex-col gap-4 p-4">
          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledInput
                    type="number"
                    {...field}
                    label="Montant (€)"
                    allowNegative={false}
                    required
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="means"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledSelectInput<PaymentMeansEnum>
                    {...field}
                    options={Object.values(PaymentMeansEnum).map((means) => ({
                      label: paymentMeansEnumMapping[means],
                      value: means
                    }))}
                    label="Moyen de remboursement"
                    selectedOption={getSelectedEnumOption(field.value, paymentMeansEnumMapping)}
                    required
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="reason"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledInput type="textarea" rows={4} {...field} label="Motif" />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <ValidationButtons buttonsConfig={buttonsConfig} hasBorderTop />
      </form>
    </Form>
  )
}

export default RefundEnrollmentForm
